/**
 * SpecialistID — Product PDP modular bundle: VOLUME TIERS + TABLE FALLBACK
 * Part of the custom-product-* set (split out of the legacy custom-product.js).
 *
 * Two self-contained units that came across from the live theme intact:
 *   1. the Regios tier resolver, exported as window.SPID_VolumeTiers, which
 *      reads the exact discount schedule from the app's own on-page config
 *      rather than the rounded percentage the app prints;
 *   2. the volume-pricing table fallback renderer, which builds the tier table
 *      locally when the app loses its DOMContentLoaded race.
 *
 * Neither touches window.SPID — they communicate through window.SPID_VolumeTiers
 * and the DOM, exactly as they did in the monolith. Kept as their own file so
 * custom-product-volume-pricing.js stays about the table the theme renders.
 *
 * LOAD ORDER: after custom-product-volume-pricing.js (updateTablePrice() calls
 * window.SPID_VolumeTiers.percentForQuantity), before custom-product.js.
 */

/* ---------------------------------------------------------------------------
   Regios tier resolver (2026-08-03)

   Reads the exact volume-discount schedule for the current product out of the
   app's own on-page config (window.RegiosDOPP.discountConfigs), the same data
   the Regios Shopify Function prices checkout from.

   Why the Price column cannot be derived from the rendered table: the app
   prints its Discount cell through the [discount_percentage] token, which is
   hardcoded to Math.round(). A 6.25% tier therefore renders as "6% off", and
   pricing that string back out quotes a figure checkout does not charge:
   6% of $0.80 floors to $0.04 ($0.76) where the real 6.25% floors to $0.05
   ($0.75). Reading the schedule here keeps the money exact while leaving the
   percentage label alone: what customers see is whatever the app renders from
   the Regios admin settings, which is where the schedule is maintained.

   Exported on window because the pricing code and the fallback renderer below
   both need it and live in different scopes. Fail-safe by design: any config
   shape that cannot be judged safely client-side resolves to null, and callers
   keep their previous behaviour.
--------------------------------------------------------------------------- */
(function () {
  function parseConfig(raw) {
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch (e) { return null; }
  }

  function sameId(a, b) {
    var na = String(a).replace(/\D/g, '');
    var nb = String(b).replace(/\D/g, '');
    return na !== '' && na === nb;
  }

  // The ids a product/variant/collection condition is scoped to. Regios keeps
  // them in different places depending on how the rule was built: the picker
  // fills selectedProducts / selectedProductVariants, while a rule saved from
  // the admin's compact form leaves those empty and carries
  // compactFormat = { "<product id>": "<variant id>" } instead. A leftover
  // tagsToCheck often sits beside both, so scanning for "the first array" picks
  // the tag list and matches nothing (the visitor badge log book rules are in
  // exactly that state). Prefer the fields that belong to this type, and fall
  // back to the old scan so rule shapes that already resolved keep working.
  function idsForEligibility(el) {
    var out = [];
    function push(v) {
      if (v == null) return;
      if (Array.isArray(v)) { for (var i = 0; i < v.length; i++) push(v[i]); return; }
      if (typeof v === 'object') return;
      out.push(v);
    }
    var cf = el.compactFormat && typeof el.compactFormat === 'object' ? el.compactFormat : null;
    if (el.type === 'SPECIFIC_PRODUCT_VARIANTS') {
      push(el.selectedProductVariants);
      if (cf) { for (var k in cf) { if (Object.prototype.hasOwnProperty.call(cf, k)) push(cf[k]); } }
    } else if (el.type === 'SPECIFIC_PRODUCTS') {
      push(el.selectedProducts);
      if (cf) { for (var k2 in cf) { if (Object.prototype.hasOwnProperty.call(cf, k2)) push(k2); } }
      push(el.asListOfIds);
    } else if (el.type === 'SPECIFIC_COLLECTIONS') {
      push(el.selectedCollections);
      push(el.asListOfIds);
    }
    if (out.length) return out;
    for (var k3 in el) { if (Array.isArray(el[k3])) return el[k3]; }
    return null;
  }

  // Returns true/false, or null when the condition cannot be evaluated
  // safely client-side (customer segments, unknown types...).
  function evalCondition(cond, pp) {
    if (!cond || typeof cond !== 'object') return null;
    if (cond.type === 'CUSTOMER_ELIGIBILITY') {
      return (cond.eligibility && cond.eligibility.type === 'ALL_CUSTOMERS') ? true : null;
    }
    if (cond.type !== 'PRODUCT_ELIGIBILITY') return null;
    var el = cond.eligibility || {};
    var matched = null;
    if (el.type === 'ALL_PRODUCTS') {
      matched = true;
    } else if (el.type === 'SPECIFIC_PRODUCT_TAGS') {
      var tags = (pp.tags || []).map(function (t) { return String(t).toLowerCase(); });
      matched = (el.tagsToCheck || []).some(function (t) {
        return tags.indexOf(String(t).toLowerCase()) !== -1;
      });
    } else {
      var ids = idsForEligibility(el);
      if (!ids) return null;
      if (el.type === 'SPECIFIC_PRODUCTS') {
        matched = ids.some(function (x) { return sameId(x, pp.productId); });
      } else if (el.type === 'SPECIFIC_PRODUCT_VARIANTS') {
        matched = ids.some(function (x) { return sameId(x, pp.variantId); });
      } else if (el.type === 'SPECIFIC_COLLECTIONS') {
        matched = (pp.collectionIds || []).some(function (c) {
          return ids.some(function (x) { return sameId(x, c); });
        });
      } else {
        return null;
      }
    }
    if (matched === null) return null;
    return cond.includeOrExclude === 'EXCLUDE' ? !matched : matched;
  }

  // The variant this page is currently showing.
  //
  // window.RegiosDOPP_ProductPage is built once, at render, and the app does not
  // refresh it when the shopper switches variant without a page load. Its
  // variantId therefore still names the PREVIOUS variant, while the app happily
  // re-renders the table's percentage labels for the new one. Keying the tier
  // lookup off that stale id priced every row from the old variant's ladder:
  // switching the visitor badge log book from 200 to 500 badges left "$66.00
  // each" on screen for a book that checks out at $109.99, a $439.90 gap on ten
  // units, with the shopper charged more than the page advertised.
  //
  // The theme knows the truth - the product form's id input is what actually
  // gets posted to /cart/add - so the app's object is used for everything
  // except the variant id, which comes from the form.
  function currentPage(pp) {
    if (pp) return pp;
    pp = window.RegiosDOPP_ProductPage;
    if (!pp) return null;
    var vid = null;
    try {
      vid = (window.SPID && typeof SPID.getVariantId === 'function') ? SPID.getVariantId() : null;
    } catch (e) {}
    if (!vid) {
      var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      vid = input && input.value;
    }
    if (!vid) return pp;
    var digits = function (x) { return String(x).replace(/[^0-9]/g, ''); };
    if (digits(vid) === '' || digits(vid) === digits(pp.variantId)) return pp;
    var clone = {};
    for (var k in pp) { if (Object.prototype.hasOwnProperty.call(pp, k)) clone[k] = pp[k]; }
    clone.variantId = vid;
    return clone;
  }

  // First enabled VOLUME/PERCENTAGE discount whose conditions this product
  // passes; skips any config shape we cannot fully judge. With allowFixed
  // (2026-09-12) a ladder of FIXED_AMOUNT breaks (a dollar amount off each
  // unit, the custom badge reel schedule) qualifies too; the default stays
  // percent-only because the volume table and percentForQuantity below
  // price rows as percentages.
  function breaksFor(pp, allowFixed, wantPrice) {
    pp = currentPage(pp);
    if (!pp) return null;
    var cfgs = (window.RegiosDOPP || {}).discountConfigs || {};
    var keys = Object.keys(cfgs);
    for (var i = 0; i < keys.length; i++) {
      var parsed = parseConfig(cfgs[keys[i]]);
      var cfg = parsed && parsed.config;
      if (!cfg || cfg.isEnabled === false) continue;
      var entry = cfg.customLogic && cfg.customLogic.entryPoint;
      if (!entry || entry.type !== 'CHECK_IF' || !entry.then) continue;
      if (entry.then.type !== 'APPLY_DISCOUNT') continue;
      var value = entry.then.value;
      if (!value || value.type !== 'VOLUME' || !Array.isArray(value.breaks) || !value.breaks.length) continue;
      var allPct = value.breaks.every(function (b) {
        return b && b.discount && b.discount.type === 'PERCENTAGE' && isFinite(parseFloat(b.discount.percentage));
      });
      var allFixed = allowFixed && value.breaks.every(function (b) {
        return b && b.discount && b.discount.type === 'FIXED_AMOUNT' && isFinite(parseFloat(b.discount.amount));
      });
      var allPrice = wantPrice && value.breaks.every(function (b) {
        return b && b.discount && b.discount.type === 'FIXED_PRICE' && isFinite(parseFloat(b.discount.price));
      });
      if (!allPct && !allFixed && !allPrice) continue;
      var conds = Array.isArray(entry.conditions) ? entry.conditions : [];
      if (!conds.length) continue;
      var pass = entry.matchType === 'OR' ? false : true;
      var unevaluable = false;
      for (var j = 0; j < conds.length; j++) {
        var r = evalCondition(conds[j], pp);
        if (r === null) { unevaluable = true; break; }
        if (entry.matchType === 'OR') { if (r) { pass = true; break; } }
        else if (!r) { pass = false; break; }
      }
      if (unevaluable || !pass) continue;
      return value.breaks;
    }
    return null;
  }

  // Exact percentage for the tier a table row represents, matched on the row's
  // own minimum quantity. Returns null when there is no such tier, which is the
  // signal for callers to keep whatever the rendered table says.
  function percentForQuantity(quantity) {
    if (!isFinite(quantity)) return null;
    var breaks = breaksFor();
    if (!breaks) return null;
    for (var i = 0; i < breaks.length; i++) {
      if (parseFloat(breaks[i].minimumQuantity) === Number(quantity)) {
        var pct = parseFloat(breaks[i].discount.percentage);
        return isFinite(pct) ? pct : null;
      }
    }
    return null;
  }

  // Exact unit price for a tier whose break names the price outright
  // (FIXED_PRICE), in dollars. Regios prints those rows through the same
  // rounded [discount_percentage] token as every other ladder, so pricing the
  // label quotes a figure checkout does not charge: the visitor badge log book
  // showed $65.80 at 10+ where checkout charges the rule's $66.00, because
  // $69.99 -> $66.00 is 5.7% and the label rounds it to 6%. Returns null for
  // any other ladder, which leaves the percentage path untouched.
  function priceForQuantity(quantity) {
    if (!isFinite(quantity)) return null;
    var breaks = breaksFor(null, false, true);
    if (!breaks) return null;
    for (var i = 0; i < breaks.length; i++) {
      if (!breaks[i].discount || breaks[i].discount.type !== 'FIXED_PRICE') return null;
      if (parseFloat(breaks[i].minimumQuantity) === Number(quantity)) {
        var p = parseFloat(breaks[i].discount.price);
        return isFinite(p) ? p : null;
      }
    }
    return null;
  }

  window.SPID_VolumeTiers = {
    currentPage: currentPage,
    breaksFor: breaksFor,
    percentForQuantity: percentForQuantity,
    priceForQuantity: priceForQuantity
  };
})();

/* ---------------------------------------------------------------------------
   Volume-pricing table fallback (2026-07-25)

   The Regios Discounts storefront script computes prices and dispatches
   "regios-dopp:price-update" exactly once, gated on DOMContentLoaded. Its
   scripts load async, so on heavier pages they can finish AFTER
   DOMContentLoaded — the dispatch then never fires, the app's volume-table
   module (loaded and listening) never renders, and the theme's volume-pricing
   kit stays hidden. The live theme currently wins this race; this theme can
   lose it (always on default-variant loads and FPD pages).

   Fallback: if the app has not rendered its table shortly after window load,
   build the identical table locally from the app's own on-page discount
   config (window.RegiosDOPP.discountConfigs — the same data the app reads).
   Display-only and fail-safe: checkout discounts are applied server-side by
   the app's Shopify Function regardless, and any config shape we cannot
   evaluate safely is skipped (kit stays hidden, same as without this code).
   The body-level "Pricing table observer" above detects the inserted table
   and runs updateTablePrice(), which prices the rows, adds the "1+" row and
   Price column, drives the progress bar, and unhides the kit — exactly as it
   does for an app-rendered table. If the app does render late after all, a
   dedupe observer removes our copy in favor of the app's.
--------------------------------------------------------------------------- */
(function () {
  var FALLBACK_DELAY_MS = 1800;

  function volumeBreaksFor(pp) {
    return (window.SPID_VolumeTiers && window.SPID_VolumeTiers.breaksFor(pp)) || null;
  }

  function renderFallbackTable() {
    if (document.querySelector('.regios-dopp-generic-volume-pricing-table')) return;
    var target = document.getElementById('regios-dopp-volume-pricing-table-target');
    // currentPage lives in the resolver block above, so reach it through its
    // export; calling it bare threw a ReferenceError here (SPID-326).
    var pp = window.SPID_VolumeTiers ? window.SPID_VolumeTiers.currentPage(null) : null;
    if (!target || !pp || !window.RegiosDOPP) return;
    if (target.childElementCount > 0) return;
    var breaks = volumeBreaksFor(pp);
    if (!breaks) return;

    var wrap = target.closest('.volume-pricing-table-wrapper') || target.parentElement;
    var tmpl = wrap ? wrap.querySelector('template') : null;
    var table = null;
    if (tmpl && tmpl.content && tmpl.content.querySelector('table')) {
      table = tmpl.content.querySelector('table').cloneNode(true);
    } else {
      table = document.createElement('table');
      table.className = 'regios-dopp-generic-volume-pricing-table';
      table.innerHTML = '<thead><tr><th scope="col">Quantity</th><th scope="col">Discount</th></tr></thead><tbody></tbody>';
    }
    table.setAttribute('data-spid-fallback', '1');
    var tbody = table.querySelector('tbody');
    breaks
      .slice()
      .sort(function (a, b) { return parseFloat(a.minimumQuantity) - parseFloat(b.minimumQuantity); })
      .forEach(function (b) {
        var tr = document.createElement('tr');
        var qtyCell = document.createElement('td');
        qtyCell.textContent = parseInt(b.minimumQuantity, 10) + '+';
        var discCell = document.createElement('td');
        discCell.textContent = Math.round(parseFloat(b.discount.percentage)) + '% off';
        tr.appendChild(qtyCell);
        tr.appendChild(discCell);
        tbody.appendChild(tr);
      });
    target.appendChild(table);

    // If the app renders its own table later after all, drop ours.
    var dedupe = new MutationObserver(function () {
      var tables = document.querySelectorAll('.regios-dopp-generic-volume-pricing-table');
      if (tables.length > 1) {
        var ours = document.querySelector('.regios-dopp-generic-volume-pricing-table[data-spid-fallback]');
        if (ours) ours.remove();
        dedupe.disconnect();
      }
    });
    dedupe.observe(target, { childList: true, subtree: true });
  }

  function armFallback() {
    setTimeout(renderFallbackTable, FALLBACK_DELAY_MS);
    setTimeout(renderFallbackTable, FALLBACK_DELAY_MS * 3); // slow pages (FPD)
  }
  if (document.readyState === 'complete') armFallback();
  else window.addEventListener('load', armFallback);

  // Announce this module so main-product.liquid can leave it out safely.
  if (window.SPID && SPID.register) SPID.register('volume-fallback');
})();